import { AutoDimensionType } from "building-types/autoDimensions/autoDimensionTypesEnum";
import type { DimensionRowLevel } from "building-types/dimensions/dimensionTypesEnum";
import type { ProvenanceCriterion } from "building-types/provenance/provenanceTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import type {
  AutoDimensionFloorPlanExteriorByOrientationHandle,
  AutoDimensionFloorPlanExteriorByRoomMultipleHandle,
  AutoDimensionFloorPlanExteriorByRoomMultipleProvenance,
  AutoDimensionFloorPlanExteriorByRoomSingleHandle,
  AutoDimensionFloorPlanInteriorByOrthogonalRoomEdge,
  AutoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandle,
  AutoDimensionFloorPlanInteriorByRoomEdgePair,
  AutoDimensionFloorPlanInteriorByRoomEdgePairHandle,
  AutoDimensionFoundationExteriorByOrientationHandle,
  AutoDimensionFoundationExteriorByRoomMultipleHandle,
  AutoDimensionFoundationExteriorByRoomMultipleProvenance,
  AutoDimensionFoundationExteriorByRoomSingleHandle,
  AutoDimensionFoundationInteriorByOrientationHandle,
  AutoDimensionFoundationInteriorByRoomMultipleHandle,
  AutoDimensionFoundationInteriorByRoomMultipleProvenance,
  AutoDimensionFoundationInteriorByRoomSingleHandle,
  AutoDimensionHandle,
  AutoDimensionPersistedHandle,
  EdgeOrientation,
  LevelRoomEdge,
  RoomEdge,
} from "./docEntityHandleTypes";
import type { DocEntityHandle, LevelHandle, RoomHandle } from "./docEntityHandleTypes";

/**
 * Generates a handle associated with room edges. The array may contain one or more room edges.
 */

// Floor Plan Exterior

export function autoDimensionFloorPlanExteriorByRoomHandleMultipleNew(
  criteria: ProvenanceCriterion[],
  levelId: string,
  roomEdges: RoomEdge[],
  rowLevel: DimensionRowLevel,
  isOrthogonal: boolean,
): AutoDimensionFloorPlanExteriorByRoomMultipleHandle {
  return {
    type: "AUTO_DIMENSION",
    provenance: {
      criteria,
      type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_MULTIPLE,
      levelId,
      roomEdges,
      rowLevel,
      isOrthogonal,
    },
  };
}

export function autoDimensionFloorPlanExteriorByOrientationHandleNew(
  criteria: ProvenanceCriterion[],
  levelId: string,
  orientation: EdgeOrientation,
  rowLevel: DimensionRowLevel,
): AutoDimensionFloorPlanExteriorByOrientationHandle {
  return {
    type: "AUTO_DIMENSION",
    provenance: {
      criteria,
      type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ORIENTATION,
      levelId,
      orientation,
      rowLevel,
    },
  };
}

// Foundation Exterior

export function autoDimensionFoundationExteriorByRoomHandleMultipleNew(
  criteria: ProvenanceCriterion[],
  roomEdges: LevelRoomEdge[],
  rowLevel: DimensionRowLevel,
  isOrthogonal: boolean,
): AutoDimensionFoundationExteriorByRoomMultipleHandle {
  return {
    type: "AUTO_DIMENSION",
    provenance: {
      criteria,
      type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_MULTIPLE,
      roomEdges,
      rowLevel,
      isOrthogonal,
    },
  };
}

export function autoDimensionFoundationExteriorByOrientationHandleNew(
  criteria: ProvenanceCriterion[],
  levelId: string,
  orientation: EdgeOrientation,
  rowLevel: DimensionRowLevel,
): AutoDimensionFoundationExteriorByOrientationHandle {
  return {
    type: "AUTO_DIMENSION",
    provenance: {
      criteria,
      type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ORIENTATION,
      levelId,
      orientation,
      rowLevel,
    },
  };
}
// Foundation Interior

export function autoDimensionFoundationInteriorByRoomHandleMultipleNew(
  criteria: ProvenanceCriterion[],
  roomEdges: LevelRoomEdge[],
  rowLevel: DimensionRowLevel,
  isOrthogonal: boolean,
): AutoDimensionFoundationInteriorByRoomMultipleHandle {
  return {
    type: "AUTO_DIMENSION",
    provenance: {
      criteria,
      type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_MULTIPLE,
      roomEdges,
      rowLevel,
      isOrthogonal,
    },
  };
}

export function autoDimensionFoundationInteriorByOrientationHandleNew(
  criteria: ProvenanceCriterion[],
  levelId: string,
  orientation: EdgeOrientation,
  rowLevel: DimensionRowLevel,
): AutoDimensionFoundationInteriorByOrientationHandle {
  return {
    type: "AUTO_DIMENSION",
    provenance: {
      criteria,
      type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ORIENTATION,
      levelId,
      orientation,
      rowLevel,
    },
  };
}

// Floor Plan Interior

export function autoDimensionFloorPlanInteriorByRoomEdgePairHandleNew(
  criteria: ProvenanceCriterion[],
  levelId: string,
  roomEdgePairs: [RoomEdge, RoomEdge][],
): AutoDimensionFloorPlanInteriorByRoomEdgePairHandle {
  return {
    type: "AUTO_DIMENSION",
    provenance: {
      criteria,
      type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_OPPOSITE_ROOM_EDGE_PAIR,
      levelId,
      roomEdgePairs,
    },
  };
}

export function autoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandleNew(
  criteria: ProvenanceCriterion[],
  levelId: string,
  roomEdges: RoomEdge[],
): AutoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandle {
  return {
    type: "AUTO_DIMENSION",
    provenance: {
      criteria,
      type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_ORTHOGONAL_ROOM_EDGE,
      levelId,
      roomEdges,
    },
  };
}

export function isAutoDimensionHandle(handle: DocEntityHandle): handle is AutoDimensionHandle {
  return handle?.type === "AUTO_DIMENSION";
}

export function unpackAutoDimensionFloorPlanExteriorMultipleHandle(
  provenance: AutoDimensionFloorPlanExteriorByRoomMultipleProvenance,
): AutoDimensionFloorPlanExteriorByRoomSingleHandle[] {
  const roomEdges = provenance?.roomEdges;

  if (!roomEdges || roomEdges.length === 0 || !provenance) {
    return [];
  }

  const { criteria, levelId, rowLevel, isOrthogonal } = provenance;

  return roomEdges.map(({ roomId, edgeId }) => ({
    type: "AUTO_DIMENSION",
    provenance: {
      type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_SINGLE,
      criteria,
      levelId,
      rowLevel,
      isOrthogonal,
      roomEdge: { roomId, edgeId },
    },
  })) as AutoDimensionFloorPlanExteriorByRoomSingleHandle[];
}

export function unpackAutoDimensionFoundationExteriorMultipleHandle(
  provenance: AutoDimensionFoundationExteriorByRoomMultipleProvenance,
): AutoDimensionFoundationExteriorByRoomSingleHandle[] {
  const roomEdges = provenance?.roomEdges;

  if (!roomEdges || roomEdges.length === 0 || !provenance) {
    return [];
  }

  const { criteria, rowLevel, isOrthogonal } = provenance;

  return roomEdges.map(({ levelId, roomId, edgeId, entityLocation }) => ({
    type: "AUTO_DIMENSION",
    provenance: {
      type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_SINGLE,
      criteria,
      rowLevel,
      isOrthogonal,
      roomEdge: { levelId, roomId, edgeId, entityLocation },
    },
  })) as AutoDimensionFoundationExteriorByRoomSingleHandle[];
}

export function unpackAutoDimensionFoundationInteriorMultipleHandle(
  provenance: AutoDimensionFoundationInteriorByRoomMultipleProvenance,
): AutoDimensionFoundationInteriorByRoomSingleHandle[] {
  const roomEdges = provenance?.roomEdges;

  if (!roomEdges || roomEdges.length === 0 || !provenance) {
    return [];
  }

  const { criteria, rowLevel, isOrthogonal } = provenance;

  return roomEdges.map(({ levelId, roomId, edgeId, entityLocation }) => ({
    type: "AUTO_DIMENSION",
    provenance: {
      type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_SINGLE,
      criteria,
      rowLevel,
      isOrthogonal,
      roomEdge: { levelId, roomId, edgeId, entityLocation },
    },
  })) as AutoDimensionFoundationInteriorByRoomSingleHandle[];
}

export function unpackAutoDimensionFloorPlanInteriorByRoomEdgePairHandle(
  provenance: AutoDimensionFloorPlanInteriorByRoomEdgePair,
): AutoDimensionFloorPlanInteriorByRoomEdgePairHandle[] {
  const roomEdgePairs = provenance?.roomEdgePairs;

  if (!roomEdgePairs || roomEdgePairs.length === 0 || !provenance) {
    return [];
  }

  const { criteria, levelId } = provenance;

  return roomEdgePairs.map((x) => ({
    type: "AUTO_DIMENSION",
    provenance: {
      type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_OPPOSITE_ROOM_EDGE_PAIR,
      criteria,
      levelId,
      roomEdgePairs: [x],
    },
  })) as AutoDimensionFloorPlanInteriorByRoomEdgePairHandle[];
}

export function unpackAutoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandle(
  provenance: AutoDimensionFloorPlanInteriorByOrthogonalRoomEdge,
): AutoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandle[] {
  const roomEdges = provenance?.roomEdges;

  if (!roomEdges || roomEdges.length === 0 || !provenance) {
    return [];
  }

  const { criteria, levelId } = provenance;

  return roomEdges.map((x) => ({
    type: "AUTO_DIMENSION",
    provenance: {
      type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_ORTHOGONAL_ROOM_EDGE,
      criteria,
      levelId,
      roomEdges: [x],
    },
  })) as AutoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandle[];
}

/**
 * Returns the parent ids of the given auto dimension handle. This can be used for
 * determining which handles are associated with a parent for deletion purposes.
 */
export function getAutoDimensionEffectiveParentHandles(
  handle: AutoDimensionPersistedHandle,
): (RoomHandle | LevelHandle)[] {
  switch (handle.provenance.type) {
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_SINGLE:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_SINGLE:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_SINGLE:
      return [{ type: "ROOM", id: handle.provenance.roomEdge.roomId }];
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ORIENTATION:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ORIENTATION:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ORIENTATION:
      return [{ type: "LEVEL", id: handle.provenance.levelId }];
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_OPPOSITE_ROOM_EDGE_PAIR:
      return handle.provenance.roomEdgePairs.flatMap((pair) => pair.map((x) => ({ type: "ROOM", id: x.roomId })));
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_ORTHOGONAL_ROOM_EDGE:
      return handle.provenance.roomEdges.map((x) => ({ type: "ROOM", id: x.roomId }));
  }
}

export function autoDimensionHandleParentEntityType(
  type: AutoDimensionPersistedHandle,
): RoomHandle["type"] | LevelHandle["type"] {
  switch (type.provenance.type) {
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_SINGLE:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_SINGLE:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_SINGLE:
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_OPPOSITE_ROOM_EDGE_PAIR:
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_ORTHOGONAL_ROOM_EDGE:
      return "ROOM";
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ORIENTATION:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ORIENTATION:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ORIENTATION:
      return "LEVEL";
  }
}

export function docAutoDimensionAssociatedHandles(handle: AutoDimensionHandle): DocEntityHandle[] {
  const { provenance } = handle;

  switch (provenance.type) {
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ORIENTATION:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ORIENTATION:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ORIENTATION: {
      return [{ type: "LEVEL", id: provenance.levelId }];
    }
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_MULTIPLE: {
      return [
        { type: "LEVEL", id: provenance.levelId },
        ...provenance.roomEdges.map((roomEdge): RoomHandle => ({ type: "ROOM", id: roomEdge.roomId })),
      ];
    }
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_SINGLE: {
      return [
        { type: "LEVEL", id: provenance.levelId },
        { type: "ROOM", id: provenance.roomEdge.roomId },
      ];
    }
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_MULTIPLE:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_MULTIPLE: {
      return provenance.roomEdges.flatMap((roomEdge) => [
        { type: "LEVEL", id: roomEdge.levelId },
        { type: "ROOM", id: roomEdge.roomId },
      ]);
    }
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_SINGLE:
    case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_SINGLE: {
      return [
        { type: "LEVEL", id: provenance.roomEdge.levelId },
        { type: "ROOM", id: provenance.roomEdge.roomId },
      ];
    }
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_OPPOSITE_ROOM_EDGE_PAIR: {
      return [
        { type: "LEVEL", id: provenance.levelId },
        ...provenance.roomEdgePairs.flatMap((pair) =>
          pair.map((roomEdge): RoomHandle => ({ type: "ROOM", id: roomEdge.roomId })),
        ),
      ];
    }
    case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_ORTHOGONAL_ROOM_EDGE: {
      return [
        { type: "LEVEL", id: provenance.levelId },
        ...provenance.roomEdges.map((roomEdge): RoomHandle => ({ type: "ROOM", id: roomEdge.roomId })),
      ];
    }
    default: {
      assertUnreachable(provenance);
    }
  }
}
